import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import {
  EuiButton,
  EuiCallOut,
  EuiCheckbox,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { getAllReleaseOutlets } from "../../api/appReleases";
import ActionDialog from "./actionDialog";
import OutletsTable from "./outletsTable";
import {
  getErrorMessage,
  getOutletId,
  isOutletOnRelease,
  isOutletPicked,
  RELEASE_QUERY_KEY,
} from "./helpers";

// Manual mode (staging and testing): replaces the stage steps.
const OutletPicker = ({ release }) => {
  const canSetOutlets = (release.allowed_actions || []).includes("set_outlets");
  const [selectedIds, setSelectedIds] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const { data: allOutlets, error, isLoading } = useQuery(
    [RELEASE_QUERY_KEY, "allOutlets", release.id],
    () => getAllReleaseOutlets(release.id),
    { retry: false },
  );

  const pickedOutlets = allOutlets ? allOutlets.filter(isOutletPicked) : [];
  const pickedKey = pickedOutlets.map(getOutletId).join(",");

  // Keep the admin's unsaved ticks through background refreshes.
  useEffect(() => {
    if (allOutlets && !isDirty) {
      setSelectedIds(pickedKey ? pickedKey.split(",").map(Number) : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedKey, Boolean(allOutlets), isDirty]);

  // Saving replaces the whole list, so never save from a list that looks incomplete.
  const listMatchesCounts =
    !allOutlets ||
    !release.counts ||
    release.counts.picked === undefined ||
    release.counts.picked === pickedOutlets.length;
  const canEdit =
    canSetOutlets && selectedIds !== null && !error && listMatchesCounts;

  const toggle = (id, checked) => {
    setIsDirty(true);
    setSelectedIds((ids) =>
      checked ? ids.concat(id) : ids.filter((other) => other !== id),
    );
  };

  const selectColumn = {
    name: "",
    width: "40px",
    render: (outlet) => {
      const id = getOutletId(outlet);
      return (
        <EuiCheckbox
          id={`release-outlet-${id}`}
          aria-label={`Select ${outlet.name}`}
          checked={selectedIds ? selectedIds.includes(id) : isOutletPicked(outlet)}
          disabled={!canEdit}
          onChange={(event) => toggle(id, event.target.checked)}
        />
      );
    },
  };

  const removedInstalled = pickedOutlets.filter(
    (outlet) =>
      selectedIds &&
      !selectedIds.includes(getOutletId(outlet)) &&
      isOutletOnRelease(outlet, release),
  );

  return (
    <>
      {error ? (
        <>
          <EuiCallOut
            size="s"
            color="danger"
            iconType="alert"
            title={`Could not load the outlet list, so it cannot be changed. ${getErrorMessage(error)}`}
          />
          <EuiSpacer size="s" />
        </>
      ) : null}
      {!listMatchesCounts ? (
        <>
          <EuiCallOut
            size="s"
            color="danger"
            iconType="alert"
            title="The outlet list does not match the picked count, so it cannot be changed. Refresh the page and try again."
          />
          <EuiSpacer size="s" />
        </>
      ) : null}
      {canSetOutlets ? (
        <>
          <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiButton
                size="s"
                fill
                isDisabled={!canEdit || !isDirty}
                isLoading={isLoading}
                onClick={() => setShowDialog(true)}
              >
                Release to selected
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size="s" color="subdued">
                {selectedIds ? selectedIds.length : 0} selected
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer size="s" />
        </>
      ) : null}
      <OutletsTable
        release={release}
        leadingColumns={canSetOutlets ? [selectColumn] : []}
      />
      {showDialog ? (
        <ActionDialog
          action="set_outlets"
          release={release}
          outletIds={selectedIds}
          warning={
            removedInstalled.length > 0
              ? `${removedInstalled
                  .map((outlet) => outlet.name)
                  .join(", ")} will go back to the old version.`
              : null
          }
          onClose={() => setShowDialog(false)}
          onDone={() => setIsDirty(false)}
        />
      ) : null}
    </>
  );
};

export default OutletPicker;
